import { useCallback, useEffect, useRef, useState } from "react";
import type { ArgusInstanceStatus } from "../../types/argus";
import { useResumeArgusInstance } from "./use-resume-argus-instance";

const MAX_AUTO_RESUME_ATTEMPTS = 4;
const INITIAL_RETRY_DELAY_MS = 5_000;
const MAX_RETRY_DELAY_MS = 60_000;

interface UseAutoResumeArgusInstanceOptions {
  instanceId?: string;
  instanceStatus?: ArgusInstanceStatus | null;
  enabled?: boolean;
}

/**
 * Resumes a paused instance with a bounded exponential retry schedule.
 * The attempt budget belongs to one instance for the lifetime of the mounted page.
 */
export function useAutoResumeArgusInstance({
  instanceId,
  instanceStatus,
  enabled = true,
}: UseAutoResumeArgusInstanceOptions) {
  const { mutateAsync } = useResumeArgusInstance();
  const [attemptCount, setAttemptCount] = useState(0);
  const previousInstanceIdRef = useRef(instanceId);

  useEffect(() => {
    const instanceChanged = previousInstanceIdRef.current !== instanceId;
    previousInstanceIdRef.current = instanceId;
    if (instanceChanged || instanceStatus === "active") {
      setAttemptCount(0);
    }
  }, [instanceId, instanceStatus]);

  const hasReachedLimit = attemptCount >= MAX_AUTO_RESUME_ATTEMPTS;

  useEffect(() => {
    if (!enabled || !instanceId || instanceStatus !== "paused" || hasReachedLimit) return;

    const delay =
      attemptCount === 0
        ? 0
        : Math.min(INITIAL_RETRY_DELAY_MS * 2 ** (attemptCount - 1), MAX_RETRY_DELAY_MS);
    let cancelled = false;
    const timeout = window.setTimeout(() => {
      void mutateAsync({ instanceId })
        .catch((error) => {
          console.warn("[argus] auto-resume failed:", error);
        })
        .finally(() => {
          if (!cancelled) {
            setAttemptCount((current) => current + 1);
          }
        });
    }, delay);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [attemptCount, enabled, hasReachedLimit, instanceId, instanceStatus, mutateAsync]);

  const retry = useCallback(() => {
    setAttemptCount(0);
  }, []);

  return {
    hasReachedLimit: instanceStatus === "paused" && hasReachedLimit,
    isAutoResuming: enabled && instanceStatus === "paused" && !hasReachedLimit,
    retry,
  };
}
